import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { ReviewApprovalItem } from "../../../../shared/types";
import { buildBrandScopedPath } from "../../lib/brand-navigation";

export function BlogRevisionFeedback({ brief, brandId }: { brief: ReviewApprovalItem; brandId: string }) {
  const [feedback, setFeedback] = useState(brief.blog_revision_run?.feedback ?? "");
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const revision = useMutation({
    mutationFn: (payload: { feedback: string; expected_parent_document_key: string; retry_run_id?: string }) => api.post<{ data: { run_id: string } }>(
      `/api/campaigns/${brief.campaign_id}/briefs/${brief.id}/blog-revision`, payload),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["brand-review", brandId] }), qc.invalidateQueries({ queryKey: ["blog-revision", brandId] })]),
  });
  const linked = brief.blog_revision_run;
  const submitting = revision.isPending;
  const canRequest = ["in_review", "drafting"].includes(brief.state) && !submitting && linked?.status !== "running";
  const failed = linked?.status === "failed";
  const blogSurface = (brief.surface ?? brief.platform ?? "").toLowerCase();
  if (blogSurface !== "blog" && blogSurface !== "website") return null;

  async function submit(retry = false) {
    const text = feedback.trim();
    if (text.length < 8 || text.length > 2000) return;
    if (!brief.draft_document_key) return;
    revision.mutate({ feedback: text, expected_parent_document_key: brief.draft_document_key,
      ...(retry && linked ? { retry_run_id: linked.run_id } : {}) });
  }

  return (
    <div className="contents">
      <button type="button" onClick={() => setOpen((value) => !value)} disabled={linked?.status === "running"}
        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-60 hover:bg-slate-50">
        {linked?.status === "running" ? "Revision running…" : "Request edits"}
      </button>
      {open || linked ? <section className="basis-full rounded-lg border border-slate-200 bg-slate-50 p-4" aria-label="Checked blog revision">
        <h3 className="text-sm font-semibold text-slate-900">Request a checked revision</h3>
        {linked ? <Link className="mt-2 inline-block text-sm font-medium text-brand-700 underline" to={buildBrandScopedPath(`/runs/${linked.run_id}`, brandId)}>
          {linked.status === "running" ? `View revision Run · ${linked.current_node_id === "draft_and_review" ? "Writing and checking" : linked.current_node_id === "save_for_review" ? "Saving for review" : "Starting"}` : `View revision Run · ${linked.status}`}
        </Link> : null}
        <>
          <label className="mt-3 block text-sm text-slate-900" htmlFor={`blog-feedback-${brief.id}`}>What should change?</label>
          <textarea id={`blog-feedback-${brief.id}`} value={feedback} onChange={(event) => setFeedback(event.target.value)} maxLength={2000}
            disabled={linked?.status === "running" || submitting || !brief.draft_document_key}
            placeholder="Describe a concrete edit, such as shortening the introduction or explaining the key term earlier."
            className="mt-1 min-h-24 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900" />
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <button type="button" disabled={!canRequest || !brief.draft_document_key || feedback.trim().length < 8} onClick={() => submit(false)}
              className="rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
              {linked?.status === "running" ? "Revision running…" : failed ? "Submit feedback" : "Request revision"}
            </button>
            {failed ? <button type="button" disabled={!canRequest || feedback.trim() !== linked?.feedback?.trim()} onClick={() => void submit(true)} className="text-sm font-medium text-brand-700 underline disabled:opacity-50">Retry failed revision</button> : null}
          </div>
          {revision.error ? <p role="alert" className="mt-2 text-sm text-red-800">{revision.error instanceof Error ? revision.error.message : "Could not submit feedback. Your text is still here; try again."}</p> : null}
          {linked?.status === "failed" ? <p className="mt-2 text-sm text-amber-900">{linked.error ? `${linked.error} ` : ""}The original draft remains available. Retry starts a new capped Run with the same feedback.</p> : null}
        </>
      </section> : null}
    </div>
  );
}
